import { useState } from 'react';
import { site, nav } from '@/data/site';
import { useScrollY } from '@/hooks/useScrollY';

export default function Header({ solid = false }: { solid?: boolean }) {
  const scrollY = useScrollY();
  const scrolled = solid || scrollY > 40;
  const [open, setOpen] = useState(false);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-xl border-b border-background-200/70 py-2.5'
          : 'bg-transparent border-b border-transparent py-3.5'
      }`}
    >
      <div className="w-full px-4 md:px-8 grid grid-cols-[auto_1fr_auto] lg:grid-cols-[1fr_auto_1fr] items-center gap-3 md:gap-4">
        {/* Logo – frei stehend, ohne Kasten (Weiß wird via multiply entfernt) */}
        <a
          href="#top"
          className="justify-self-start flex items-center shrink-0 cursor-pointer"
          aria-label="Arnold Gartenpflege – zum Seitenanfang"
        >
          <img
            src={site.logo}
            alt="Arnold Gartenpflege – Logo mit Baum und Sonnenblättern"
            title="Arnold Gartenpflege"
            className={`h-9 md:h-11 w-auto object-contain transition-all duration-500 ${
              scrolled ? '[mix-blend-mode:multiply]' : 'brightness-0 invert'
            }`}
          />
        </a>

        {/* Nav – exakt mittig */}
        <nav
          className="hidden lg:flex justify-self-center items-center gap-1"
          aria-label="Hauptnavigation"
        >
          {nav.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={`px-4 py-2 rounded-full font-label text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:bg-primary-50 hover:text-primary-700'
                  : 'text-background-50/90 hover:bg-background-50/15 hover:text-background-50'
              }`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Right – nur Anruf-Button */}
        <div className="justify-self-end flex items-center gap-2 md:gap-3">
          <a
            href={site.phoneHref}
            className="inline-flex items-center gap-2 rounded-md bg-accent-500 hover:bg-accent-600 text-foreground-950 font-label text-sm font-bold px-3.5 md:px-5 py-2.5 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill" />
            <span className="hidden sm:inline">{site.phoneDisplay}</span>
          </a>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menü öffnen"
            aria-expanded={open}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-xl transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-900 hover:bg-background-200' : 'text-background-50 hover:bg-background-50/15'
            }`}
          >
            <i className={open ? 'ri-close-line' : 'ri-menu-line'} />
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      <div className={`lg:hidden overflow-hidden transition-all duration-500 ${open ? 'max-h-96' : 'max-h-0'}`}>
        <nav
          className="mx-4 mt-3 mb-1 rounded-lg border border-background-200/80 bg-background-50 p-2 flex flex-col"
          aria-label="Mobile Navigation"
        >
          {nav.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={() => setOpen(false)}
              className="px-4 py-3 rounded-md font-label text-sm font-semibold text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
            >
              {item.label}
            </a>
          ))}
          <a
            href={site.phoneHref}
            onClick={() => setOpen(false)}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 text-foreground-950 font-label text-sm font-bold px-4 py-3 cursor-pointer"
          >
            <i className="ri-phone-fill" />
            {site.phoneDisplay}
          </a>
        </nav>
      </div>
    </header>
  );
}